import React, { useState } from 'react';

import { saveCommunitiesEmailNotificationRecipients } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import SettingsPage from 'components/Settings/SettingsPage';
import { CommunityPreferenceNotification, CommunityPreferenceType } from 'types/communities';

import CommunitySettingLabel from '../../CommunitySettingLabel';
import {
  COMMUNITY_LABEL_COLOR,
  COMMUNITY_SETTINGS_PAGE_TYPE,
  EMAIL_NOTIFICATION_TYPE,
  ONLINE_REVIEWS_DIGEST,
} from '../../constants';
import EmailNotificationsRecipientsModal from '../EmailNotificationsRecipientsModal';

const getItemStatusComponent = (item: CommunityPreferenceType) => {
  const { statusElements } = ONLINE_REVIEWS_DIGEST;
  const preference = item.preferences?.online_reviews_digest_notifications;
  const activeStatus = Object.values(statusElements).find(({ value }) => value === preference);

  if (activeStatus && activeStatus.value !== statusElements.OFF.value) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
        <CommunitySettingLabel color={COMMUNITY_LABEL_COLOR.POSITIVE}>{activeStatus.label}</CommunitySettingLabel>
        <span style={{ color: 'var(--gray-dark)', fontSize: 12 }}>
          {item.receiving_notification_users_no}/{item.total_users_no} users receiving
        </span>
      </div>
    );
  }

  return (
    <CommunitySettingLabel color={COMMUNITY_LABEL_COLOR.NEUTRAL}>{statusElements.OFF.label}</CommunitySettingLabel>
  );
};

const OnlineReviewsDigest = () => {
  const { showMessage } = useRootContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [communitiesToModify, setCommunitiesToModify] = useState<CommunityPreferenceType[]>([]);
  const [refetchCommunities, setRefetchCommunities] = useState<() => void | undefined>();
  const [clearSelection, setClearSelection] = useState<() => void | undefined>();

  const showCommunitiesEditModal = (items: any[]) => {
    setCommunitiesToModify(items);
    setIsModalOpen(true);
  };

  const savePreferenceRecipients = (newStatus: number, preferenceNotifications: CommunityPreferenceNotification[]) => {
    setLoading(true);

    const payload = {
      communities: preferenceNotifications,
      email_notifications_enabled: newStatus,
    };

    saveCommunitiesEmailNotificationRecipients(EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST, payload)
      .then(() => {
        showMessage('success', 'All selected communities updated successfully');
        if (refetchCommunities) refetchCommunities();
        if (clearSelection) clearSelection();
      })
      .catch(() => {
        showMessage('error', 'Failed to update one or more communities!');
      })
      .finally(() => {
        setLoading(false);
        setIsModalOpen(false);
      });
  };

  return (
    <>
      <SettingsPage
        filterOptions={Object.values(ONLINE_REVIEWS_DIGEST.statusElements)}
        getItemStatusComponent={getItemStatusComponent}
        showCommunitiesEditModal={showCommunitiesEditModal}
        settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.ONLINE_REVIEWS_DIGEST}
        setRefetchCommunities={setRefetchCommunities}
        setClearSelection={setClearSelection}
        filterField={ONLINE_REVIEWS_DIGEST.field}
        notificationType={EMAIL_NOTIFICATION_TYPE.ONLINE_REVIEWS_DIGEST}
      />
      <EmailNotificationsRecipientsModal
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        loading={loading}
        title={ONLINE_REVIEWS_DIGEST.title}
        communitiesToModify={communitiesToModify}
        onSave={savePreferenceRecipients}
        settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.ONLINE_REVIEWS_DIGEST}
      />
    </>
  );
};

export default OnlineReviewsDigest;
